import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, Tenant } from '@/types'
import { initialState, type ModuleSettings } from './state'
import { getters } from './getters'
import { createActions } from './actions'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(initialState.user)
  const isAuthenticated = computed(() => user.value !== null)
  const isLoading = ref(initialState.isLoading)
  const error = ref<string | null>(initialState.error)
  const initialized = ref(initialState.initialized)
  const currentTenantData = ref<Tenant | null>(initialState.currentTenant)
  const impersonatingTenantId = ref<string | null>(initialState.impersonatingTenantId)
  const moduleSettings = ref<ModuleSettings>({ ...initialState.moduleSettings })

  // State setters
  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (newError: string | null) => {
    error.value = newError
  }

  const setInitialized = (init: boolean) => {
    initialized.value = init
  }

  const setCurrentTenant = (tenant: Tenant | null) => {
    currentTenantData.value = tenant
  }

  const setImpersonatingTenantId = (tenantId: string | null) => {
    impersonatingTenantId.value = tenantId
  }

  const setModuleSettings = (settings: ModuleSettings) => {
    moduleSettings.value = settings
  }

  // Create reactive state for getters
  const state = computed(() => ({
    user: user.value,
    isAuthenticated: isAuthenticated.value,
    isLoading: isLoading.value,
    error: error.value,
    initialized: initialized.value,
    currentTenant: currentTenantData.value,
    impersonatingTenantId: impersonatingTenantId.value,
    moduleSettings: moduleSettings.value,
  }))

  // Getters
  const isSuperAdmin = computed(() => getters.isSuperAdmin(state.value))
  const isTenantAdmin = computed(() => getters.isTenantAdmin(state.value))
  const isAdmin = computed(() => getters.isAdmin(state.value))
  const isOperator = computed(() => getters.isOperator(state.value))
  const isClient = computed(() => getters.isClient(state.value))
  const isMember = computed(() => getters.isMember(state.value))
  const isOrganizationOwner = computed(() => getters.isOrganizationOwner(state.value))
  const userRole = computed(() => getters.userRole(state.value))
  const userId = computed(() => getters.userId(state.value))
  const userTenantId = computed(() => getters.userTenantId(state.value))
  const userOrganizationId = computed(() => getters.userOrganizationId(state.value))
  const operatorOrganizationIds = computed(() => getters.operatorOrganizationIds(state.value))
  const userDisplayName = computed(() => getters.userDisplayName(state.value))
  const userEmail = computed(() => getters.userEmail(state.value))
  const userLanguage = computed(() => getters.userLanguage(state.value))
  const userTheme = computed(() => getters.userTheme(state.value))
  const canManageUsers = computed(() => getters.canManageUsers(state.value))
  const canManageOrganizations = computed(() => getters.canManageOrganizations(state.value))
  const canManageCRM = computed(() => getters.canManageCRM(state.value))
  const canManageSalesOrders = computed(() => getters.canManageSalesOrders(state.value))
  const canManageSettings = computed(() => getters.canManageSettings(state.value))
  const canCreateProjects = computed(() => getters.canCreateProjects(state.value))
  const canDeleteProjects = computed(() => getters.canDeleteProjects(state.value))
  const canAssignOperators = computed(() => getters.canAssignOperators(state.value))
  const canManageTenants = computed(() => getters.canManageTenants(state.value))
  const canManageOrganizationMembers = computed(() => getters.canManageOrganizationMembers(state.value))
  const hasInternalAccess = computed(() => getters.hasInternalAccess(state.value))
  const currentTenant = computed(() => getters.currentTenant(state.value))
  const tenantFeatures = computed(() => getters.tenantFeatures(state.value))
  const isImpersonating = computed(() => getters.isImpersonating(state.value))
  const effectiveTenantId = computed(() => getters.effectiveTenantId(state.value))
  const isModuleEnabled = computed(() => getters.isModuleEnabled(state.value))

  // Reactive state proxy for actions (state.value would be a static snapshot)
  const reactiveState: import('./state').AuthState = {
    get user() { return user.value },
    get isAuthenticated() { return isAuthenticated.value },
    get isLoading() { return isLoading.value },
    get error() { return error.value },
    get initialized() { return initialized.value },
    get currentTenant() { return currentTenantData.value },
    get impersonatingTenantId() { return impersonatingTenantId.value },
    get moduleSettings() { return moduleSettings.value },
  }

  // Actions
  const actions = createActions(
    reactiveState,
    setUser,
    setLoading,
    setError,
    setInitialized,
    setCurrentTenant,
    setImpersonatingTenantId,
    setModuleSettings,
  )

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    initialized,
    impersonatingTenantId,

    // Getters
    isSuperAdmin,
    isTenantAdmin,
    isAdmin,
    isOperator,
    isClient,
    isMember,
    isOrganizationOwner,
    userRole,
    userId,
    userTenantId,
    userOrganizationId,
    operatorOrganizationIds,
    userDisplayName,
    userEmail,
    userLanguage,
    userTheme,
    canManageUsers,
    canManageOrganizations,
    canManageCRM,
    canManageSalesOrders,
    canManageSettings,
    canCreateProjects,
    canDeleteProjects,
    canAssignOperators,
    canManageTenants,
    canManageOrganizationMembers,
    hasInternalAccess,
    currentTenant,
    tenantFeatures,
    isImpersonating,
    effectiveTenantId,
    isModuleEnabled,

    // Actions
    ...actions,
  }
})

export type AuthStore = ReturnType<typeof useAuthStore>
